import React, { useRef } from 'react';
import { IoCloseOutline, IoSparklesOutline } from 'react-icons/io5';
import { activeSuggestion } from './fileLibrary';

export function suggestionCopy(suggestion) {
  const newCount = suggestion?.new_folders?.length || 0;
  return {
    isNew: newCount > 0,
    label: newCount > 1 ? 'Suggested new folders:' : newCount === 1 ? 'Suggested new folder:' : 'Suggested:',
    action: newCount > 0 ? 'Create & move' : 'Move',
  };
}

// The suggestion line unmounts when its action lands: keep focus in the
// browser (on the file's row when it is still listed).
function keepFocusAfter(element, work) {
  const row = element?.closest('[data-file-id]');
  const panel = element?.closest('.fl-detail');
  const root = element?.closest('.fl-modal');
  Promise.resolve(work).finally(() => {
    const active = document.activeElement;
    if (active && active !== document.body && active.isConnected) return;
    const target = (row?.isConnected && row.querySelector('.fl-row-name'))
      || (panel?.isConnected && panel.querySelector('.fl-detail-actions button'))
      || (root?.isConnected && root);
    target?.focus?.();
  });
}

/**
 * Where Atlas suggests an unfiled file goes, with Move (or Create & move when
 * the suggestion makes new folders) and dismiss. variant: 'row' | 'panel'.
 * busy: 'accept' | 'dismiss' while that action runs.
 */
export default function FolderSuggestion({ file, busy, onAccept, onDismiss, variant = 'row' }) {
  const ref = useRef(null);
  const suggestion = activeSuggestion(file);
  if (!suggestion || !onAccept) return null;
  const { isNew, label, action } = suggestionCopy(suggestion);
  const tooltip = suggestion.reason ? `${suggestion.path}\n${suggestion.reason}` : suggestion.path;
  const run = (handler) => (event) => {
    event.stopPropagation();
    if (busy || !handler) return;
    keepFocusAfter(ref.current, handler(file));
  };

  return (
    <div
      ref={ref}
      className={`fl-suggest is-${variant}${isNew ? ' is-new' : ''}`}
      // Clicks here act on the suggestion, not the row (which opens details).
      onClick={(e) => e.stopPropagation()}
    >
      <IoSparklesOutline className="fl-suggest-icon" size={14} aria-hidden="true" />
      <span className="fl-suggest-text" title={tooltip}>
        <span className="fl-suggest-label">{label}</span>
        {' '}
        <span className="fl-suggest-path">{suggestion.path}</span>
        {suggestion.reason ? (
          <>
            <span className="fl-suggest-sep" aria-hidden="true"> · </span>
            <span className="fl-suggest-reason">{suggestion.reason}</span>
          </>
        ) : null}
      </span>
      <span className="fl-suggest-actions">
        <button
          type="button"
          className="fl-btn fl-btn-sm fl-btn-primary"
          disabled={Boolean(busy)}
          aria-label={`${action} ${file.filename} to ${suggestion.path}`}
          onClick={run(onAccept)}
        >
          {busy === 'accept' ? <span className="fl-spinner fl-spinner-xs" aria-hidden="true" /> : null}
          {action}
        </button>
        {onDismiss ? (
          <button
            type="button"
            className="fl-icon-btn fl-icon-btn-sm fl-suggest-dismiss"
            disabled={Boolean(busy)}
            aria-label={`Dismiss the folder suggestion for ${file.filename}`}
            title="Dismiss suggestion"
            onClick={run(onDismiss)}
          >
            {busy === 'dismiss' ? <span className="fl-spinner fl-spinner-xs" aria-hidden="true" /> : <IoCloseOutline size={15} />}
          </button>
        ) : null}
      </span>
    </div>
  );
}
